import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import CrudPage from "../components/CrudPage";
import { Plus, Trash2, Edit2, Download, DatabaseBackup, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PushNotificationsCard from "../components/PushNotificationsCard";
import { DailyDigestCard } from "../components/DailyDigestCard";
import { CheckEmailCard } from "../components/vehicle-check/CheckEmailCard";

export default function Impostazioni() {
  const { user } = useAuth();
  const [company, setCompany] = useState(null);

  useEffect(() => { api.get("/settings/company").then(r=>setCompany(r.data)); }, []);

  const saveCompany = async () => {
    try { await api.patch("/settings/company", company); toast.success("Salvato"); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="impostazioni-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Impostazioni</h1>

      <Tabs defaultValue="company">
        <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
          <TabsTrigger value="company" data-testid="tab-company">Azienda</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger value="operators" data-testid="tab-operators">Operatori</TabsTrigger>}
          <TabsTrigger value="users" data-testid="tab-users">Utenti & Ruoli</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">Tipologie Servizio</TabsTrigger>
          <TabsTrigger value="notifiche" data-testid="tab-notifiche">Notifiche automatiche</TabsTrigger>
          {user?.role === "admin" && <TabsTrigger value="data" data-testid="tab-data">Dati & Backup</TabsTrigger>}
        </TabsList>

        <TabsContent value="company" className="mt-4">
          {company && (
            <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nome Azienda"><Input value={company.company_name || ""} onChange={e=>setCompany({...company, company_name:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Partita IVA"><Input value={company.vat || ""} onChange={e=>setCompany({...company, vat:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Codice Fiscale"><Input value={company.fiscal_code || ""} onChange={e=>setCompany({...company, fiscal_code:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Telefono"><Input value={company.phone || ""} onChange={e=>setCompany({...company, phone:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Email"><Input type="email" value={company.email || ""} onChange={e=>setCompany({...company, email:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="PEC"><Input type="email" value={company.pec || ""} onChange={e=>setCompany({...company, pec:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
              <div className="md:col-span-2">
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Logo aziendale</Label>
                <div className="mt-1.5 flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-950/60 p-3">
                  <div className="w-24 h-24 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center overflow-hidden shrink-0">
                    {company.logo_url ? (
                      <img data-testid="logo-preview" src={company.logo_url} alt="Logo" className="max-w-full max-h-full object-contain"/>
                    ) : (
                      <span className="text-slate-600 text-[10px] font-mono-num uppercase tracking-widest">No logo</span>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      data-testid="logo-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        if (file.size > 2*1024*1024) { toast.error("Logo troppo grande (max 2 MB)"); e.target.value=""; return; }
                        const fd = new FormData(); fd.append("file", file);
                        try {
                          const r = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          setCompany({...company, logo_url: r.data.logo_url});
                          try { window.dispatchEvent(new Event("company-logo-updated")); } catch {}
                          toast.success("Logo caricato");
                        } catch(err) { toast.error(formatApiError(err)); }
                        e.target.value = "";
                      }}
                      className="block w-full text-xs text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border file:border-amber-500/40 file:bg-amber-500/10 file:text-amber-300 file:font-bold file:uppercase file:text-[10px] file:tracking-widest hover:file:bg-amber-500/20 file:cursor-pointer"
                    />
                    <div className="flex items-center gap-2">
                      {company.logo_url && (
                        <Button data-testid="logo-remove-btn" size="sm" variant="outline" onClick={async () => {
                          if (!window.confirm("Rimuovere il logo caricato?")) return;
                          try { await api.delete("/settings/logo"); setCompany({...company, logo_url: ""}); try { window.dispatchEvent(new Event("company-logo-updated")); } catch {} toast.success("Logo rimosso"); }
                          catch(err) { toast.error(formatApiError(err)); }
                        }} className="border-rose-500/30 bg-transparent text-rose-400 hover:bg-rose-500/10 h-7 text-[10px] uppercase tracking-widest font-bold">Rimuovi logo</Button>
                      )}
                      <div className="text-[10px] text-slate-500 font-mono-num">PNG · JPG · SVG · max 2 MB · se vuoto viene usato lo stemma Striano NCC luxury</div>
                    </div>
                  </div>
                </div>
              </div>
              <Field label="Indirizzo" className="md:col-span-2"><Input value={company.address || ""} onChange={e=>setCompany({...company, address:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Aliquota IVA %"><Input type="number" step="0.5" value={company.vat_rate ?? 10} onChange={e=>setCompany({...company, vat_rate:Number(e.target.value)})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Quota autista servizi %"><Input type="number" step="1" value={company.driver_share_rate ?? 50} onChange={e=>setCompany({...company, driver_share_rate:Number(e.target.value)})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Commissione Uber %"><Input type="number" step="0.5" value={company.uber_commission_rate ?? 10} onChange={e=>setCompany({...company, uber_commission_rate:Number(e.target.value)})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Quota autista Uber %"><Input type="number" step="1" value={company.uber_driver_share_rate ?? 50} onChange={e=>setCompany({...company, uber_driver_share_rate:Number(e.target.value)})} className="bg-slate-900 border-slate-800"/></Field>
              <Field label="Numero WhatsApp Alert Scadenze (+39...)"><Input value={company.alert_phone || ""} onChange={e=>setCompany({...company, alert_phone:e.target.value})} className="bg-slate-900 border-slate-800" data-testid="alert-phone-input"/></Field>
              <Field label="Giorni preavviso scadenze"><Input type="number" min="1" value={company.alert_days_before ?? 30} onChange={e=>setCompany({...company, alert_days_before:Number(e.target.value)})} className="bg-slate-900 border-slate-800"/></Field>
              <div className="md:col-span-2 border-t border-slate-800 pt-3 mt-2">
                <div className="text-xs uppercase tracking-widest text-amber-400 font-mono-num mb-2">Cedolino Autista — Note e Comunicazioni</div>
                <textarea
                  data-testid="cedolino-notes-input"
                  value={company.cedolino_notes ?? ""}
                  onChange={e=>setCompany({...company, cedolino_notes: e.target.value})}
                  rows={4}
                  maxLength={400}
                  placeholder="Grazie per la collaborazione e la professionalità dimostrata nel corso del mese. Per qualsiasi dubbio contatta l'ufficio."
                  className="w-full bg-slate-900 border border-slate-800 rounded-md px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-500/60 resize-none"
                />
                <div className="text-[10px] text-slate-500 mt-1">Testo mostrato nel PDF Cedolino nella card "Note e Comunicazioni". Se lasciato vuoto, viene usato il testo di default. Max 400 caratteri (~7 righe).</div>
              </div>
              <div className="md:col-span-2 border-t border-slate-800 pt-3 mt-2">
                <div className="text-xs uppercase tracking-widest text-amber-400 font-mono-num mb-3">Twilio WhatsApp (opzionale — per-operatore)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Twilio Account SID"><Input value={company.twilio_sid || ""} onChange={e=>setCompany({...company, twilio_sid:e.target.value})} className="bg-slate-900 border-slate-800" placeholder="AC..." data-testid="twilio-sid-input"/></Field>
                  <Field label="Twilio Auth Token"><Input type="password" value={company.twilio_token || ""} onChange={e=>setCompany({...company, twilio_token:e.target.value})} className="bg-slate-900 border-slate-800" placeholder="********" data-testid="twilio-token-input"/></Field>
                  <Field label="Twilio Numero mittente" className="md:col-span-2"><Input value={company.twilio_from || ""} onChange={e=>setCompany({...company, twilio_from:e.target.value})} className="bg-slate-900 border-slate-800" placeholder="whatsapp:+393XXXXXXXXX" data-testid="twilio-from-input"/></Field>
                </div>
                <div className="text-xs text-slate-500 mt-2">Se lasciati vuoti, si usano le credenziali globali dall'admin. Puoi crearli su console.twilio.com.</div>
              </div>
              <div className="md:col-span-2 flex flex-wrap justify-between items-center gap-3 pt-2 border-t border-slate-800">
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300 hover:bg-slate-900"
                    data-testid="test-whatsapp-btn"
                    onClick={async ()=>{
                      try {
                        const r = await api.post("/tasks/test-whatsapp", { phone: company.alert_phone || "" });
                        if (r.data?.result?.status === "sent") toast.success("Messaggio test inviato!");
                        else if (r.data?.result?.status === "mocked") toast.info("Twilio non configurato — messaggio loggato");
                        else toast.error("Errore invio: " + (r.data?.result?.error || "sconosciuto"));
                      } catch(e){ toast.error(formatApiError(e)); }
                    }}>Invia WhatsApp Test</Button>
                  <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300 hover:bg-slate-900"
                    data-testid="run-expiration-check-btn"
                    onClick={async ()=>{
                      try {
                        const r = await api.post("/tasks/run-expiration-check");
                        toast.success(`Scan completato: ${r.data.total} scadenze, ${r.data.sent} inviate, ${r.data.skipped} già notificate`);
                      } catch(e){ toast.error(formatApiError(e)); }
                    }}>Esegui Controllo Scadenze Ora</Button>
                </div>
                <Button data-testid="save-company-btn" onClick={saveCompany} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">Salva</Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          {user?.role === "admin" || user?.role === "operatore" || user?.role === "admin_azienda" ? <UsersPanel scopedToOperator={user?.role === "operatore" || user?.role === "admin_azienda"} isCompanyAdmin={user?.role === "admin_azienda"}/> : <div className="text-slate-500">Non autorizzato.</div>}
        </TabsContent>

        <TabsContent value="operators" className="mt-4">
          {user?.role === "admin" ? <OperatorsPanel/> : <div className="text-slate-500">Solo admin.</div>}
        </TabsContent>

        <TabsContent value="data" className="mt-4">
          {user?.role === "admin" ? <DataPanel/> : <div className="text-slate-500">Solo admin.</div>}
        </TabsContent>

        <TabsContent value="services" className="mt-4">
          <CrudPage
            title="Tipologie Servizio" endpoint="/services-catalog" testidPrefix="services-catalog"
            fields={[
              { key: "name", label: "Nome" },
              { key: "description", label: "Descrizione", fullWidth: true, type: "textarea" },
              { key: "default_price", label: "Prezzo predefinito", type: "number" },
            ]}
            columns={[
              { key: "name", label: "Nome" },
              { key: "description", label: "Descrizione" },
              { key: "default_price", label: "Prezzo" },
            ]}
          />
        </TabsContent>

        <TabsContent value="notifiche" className="mt-4 space-y-4">
          <PushNotificationsCard/>
          {user?.role !== "autista" && <CheckEmailCard/>}
          {["admin", "admin_azienda"].includes(user?.role) && <DailyDigestCard/>}
          <NotifichePanel/>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children, className="" }) {
  return <div className={className}><Label className="text-xs text-slate-500 mb-1 block">{label}</Label>{children}</div>;
}

// =====================================================================
// NotifichePanel — Centro Notifiche Automatiche Driver (WhatsApp + Email)
// Riusa settings.auto_notifications + log tramite /api/notifications/log
// =====================================================================
const EVENTS_META = [
  { key: "new_service",       label: "Nuovo servizio",       tone: "high",   desc: "WhatsApp + Email quando viene assegnato un nuovo servizio al driver." },
  { key: "service_modified",  label: "Modifica servizio",    tone: "high",   desc: "Invio notifica alle modifiche reali (orario, tratta, note, ecc.)." },
  { key: "service_cancelled", label: "Annullamento servizio", tone: "high",  desc: "Avvisa il driver quando il servizio viene annullato o eliminato." },
  { key: "service_reminder",  label: "Promemoria servizio",  tone: "normal", desc: "Promemoria orario automatico N ore prima (via cron)." },
  { key: "exchange_assigned", label: "Scambio servizio",     tone: "high",   desc: "Quando ricevi uno scambio e assegni un driver, lui riceve la notifica." },
  { key: "shift_changed",     label: "Cambio turno",         tone: "normal", desc: "Notifica all'assegnazione o modifica del turno." },
  { key: "cedolino_ready",    label: "Cedolino disponibile", tone: "normal", desc: "Trigger manuale admin: avvisa che il cedolino mensile è pubblicato." },
];
const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  email:    { label: "Email",    cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
};
const STATUS_META = {
  sent:     { label: "✓ Inviato",  cls: "text-emerald-400" },
  mocked:   { label: "⚠ Mock",     cls: "text-amber-400" },
  skipped:  { label: "— Skipped",  cls: "text-slate-400" },
  error:    { label: "✕ Errore",   cls: "text-rose-400" },
};

function NotifichePanel() {
  const [company, setCompany] = useState(null);
  const [log, setLog] = useState([]);
  const [filters, setFilters] = useState({ event_type: "all", channel: "all", status: "all" });
  const [busy, setBusy] = useState(false);
  const [testPhone, setTestPhone] = useState("");

  const loadAll = async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/settings/company"),
        api.get("/notifications/log", { params: { limit: 200 } }),
      ]);
      setCompany(c.data);
      setLog(l.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadAll(); }, []);

  const auto = (company && company.auto_notifications) || {
    new_service: true, service_modified: true, service_cancelled: true, service_reminder: true,
    exchange_assigned: true, shift_changed: true, cedolino_ready: true, reminder_hours: 24,
  };

  const toggle = async (k, v) => {
    if (!company) return;
    setBusy(true);
    try {
      const next = { ...auto, [k]: v };
      await api.patch("/settings/company", { auto_notifications: next });
      setCompany({ ...company, auto_notifications: next });
      toast.success("Impostazione aggiornata");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const triggerReminders = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tasks/send-booking-reminders", {});
      toast.success(`Promemoria eseguito · inviati ${r.data?.sent || 0}, skip ${r.data?.skipped || 0}`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const sendTestWA = async () => {
    if (!testPhone) return toast.error("Inserisci un numero WhatsApp");
    try {
      const r = await api.post("/tasks/test-whatsapp", { phone: testPhone });
      toast.success(`Esito test: ${r.data?.result?.status || "?"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = log.filter(e => {
    if (filters.event_type !== "all" && e.type !== filters.event_type) return false;
    if (filters.channel !== "all" && e.channel !== filters.channel) return false;
    if (filters.status !== "all" && e.status !== filters.status) return false;
    return true;
  });

  return (
    <div className="space-y-5" data-testid="notifiche-panel">
      {/* HEADER */}
      <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white">Notifiche automatiche driver</h2>
            <div className="text-sm text-slate-400 mt-1">WhatsApp + Email automatici sugli eventi del gestionale. Nessuna informazione amministrativa riservata viene inoltrata al driver.</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="trigger-reminders-btn" onClick={triggerReminders} disabled={busy}
                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold">
              Esegui promemoria ora
            </Button>
          </div>
        </div>
      </div>

      {/* TOGGLES EVENTI */}
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-4 font-bold">Eventi</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {EVENTS_META.map(ev => {
            const enabled = auto[ev.key] !== false;
            return (
              <div key={ev.key}
                   className={`rounded-xl border p-4 flex items-start gap-3 ${enabled ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-800 bg-slate-950/40"}`}
                   data-testid={`notif-toggle-row-${ev.key}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">{ev.label}</span>
                    {ev.tone === "high" && <span className="text-[9px] uppercase tracking-widest text-rose-400 font-mono-num font-bold bg-rose-500/10 border border-rose-500/40 rounded px-1.5 py-0.5">Alta</span>}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{ev.desc}</div>
                </div>
                <button
                  data-testid={`notif-toggle-${ev.key}`}
                  onClick={() => toggle(ev.key, !enabled)}
                  disabled={busy}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors border ${enabled ? "bg-emerald-500 border-emerald-400" : "bg-slate-800 border-slate-700"}`}>
                  <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"} mt-0.5`}/>
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Ore anticipo promemoria (default 24)">
            <Input type="number" min="1" max="72" data-testid="reminder-hours"
                   value={auto.reminder_hours || 24}
                   onChange={async (e)=> { const n = Math.max(1, Math.min(72, parseInt(e.target.value) || 24)); setCompany({...company, auto_notifications: {...auto, reminder_hours: n}}); }}
                   onBlur={()=> toggle("reminder_hours", auto.reminder_hours || 24)}
                   className="bg-slate-900 border-slate-800"/>
          </Field>
          <Field label="Test rapido WhatsApp (numero intero con prefisso)">
            <div className="flex gap-2">
              <Input placeholder="+393335551234" value={testPhone} onChange={e=>setTestPhone(e.target.value)} data-testid="test-wa-phone" className="bg-slate-900 border-slate-800"/>
              <Button data-testid="test-wa-btn" onClick={sendTestWA} className="bg-slate-800 border border-slate-700 text-amber-300">Test</Button>
            </div>
          </Field>
        </div>
      </div>

      {/* STORICO */}
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num font-bold">Storico notifiche</div>
          <div className="flex gap-2 flex-wrap">
            <Select value={filters.event_type} onValueChange={(v)=>setFilters({...filters, event_type: v})}>
              <SelectTrigger data-testid="filter-event" className="bg-slate-900 border-slate-800 h-9 w-44"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">
                <SelectItem value="all">Tutti gli eventi</SelectItem>
                {EVENTS_META.map(e=><SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                <SelectItem value="driver_assignment">Legacy driver_assignment</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.channel} onValueChange={(v)=>setFilters({...filters, channel: v})}>
              <SelectTrigger data-testid="filter-channel" className="bg-slate-900 border-slate-800 h-9 w-32"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">
                <SelectItem value="all">Tutti i canali</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v)=>setFilters({...filters, status: v})}>
              <SelectTrigger data-testid="filter-status" className="bg-slate-900 border-slate-800 h-9 w-32"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-slate-950 border-slate-800">
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="sent">Inviati</SelectItem>
                <SelectItem value="mocked">Mock</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
                <SelectItem value="error">Errori</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadAll} className="border-slate-800 bg-transparent text-slate-300 h-9">Aggiorna</Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
              <tr><th className="p-2 text-left">Data</th><th className="text-left">Driver</th><th className="text-left">Evento</th><th className="text-left">Canale</th><th className="text-left">Stato</th><th className="text-left">Dettaglio</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Nessun log</td></tr>}
              {filtered.slice(0, 150).map((e, idx) => {
                const ch = CHANNEL_META[e.channel] || { label: e.channel, cls: "bg-slate-800 text-slate-300 border-slate-700" };
                const st = STATUS_META[e.status] || { label: e.status || "-", cls: "text-slate-400" };
                return (
                  <tr key={e.id || idx} className="hover:bg-slate-900/40" data-testid={`log-row-${idx}`}>
                    <td className="p-2 font-mono-num text-xs text-slate-300">{(e.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="text-slate-200 text-xs">{e.driver_name || e.driver_id?.slice(0, 8) || "-"}</td>
                    <td className="text-slate-300 text-xs">{EVENTS_META.find(x=>x.key===e.type)?.label || e.type}</td>
                    <td><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-bold uppercase ${ch.cls}`}>{ch.label}</span></td>
                    <td><span className={`font-mono-num text-xs font-bold ${st.cls}`}>{st.label}</span></td>
                    <td className="text-slate-400 text-xs max-w-[300px] truncate" title={JSON.stringify(e.detail || {})}>{e.detail?.reason || e.detail?.error || e.detail?.sid || e.detail?.email_id || "-"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OperatorsPanel() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ business_name:"", contact_name:"", email:"", phone:"", vat:"", fiscal_code:"", address:"", login_password:"", login_name:"" });
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const r = await api.get("/operators"); setRows(r.data); } catch(e){} };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ business_name:"", contact_name:"", email:"", phone:"", vat:"", fiscal_code:"", address:"", login_password:"", login_name:"" }); setOpen(true); };
  const openEdit = (o) => { setEditing(o); setForm({ ...o, login_password:"" }); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const { login_password, ...rest } = form;
        await api.patch(`/operators/${editing.id}`, rest);
        toast.success("Operatore aggiornato");
      } else {
        if (!form.login_password || form.login_password.length < 6) { toast.error("Password minimo 6 caratteri"); setBusy(false); return; }
        await api.post("/operators", form);
        toast.success("Operatore creato");
      }
      setOpen(false); load();
    } catch(e){ toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const resetPassword = async (o) => {
    const p = prompt(`Nuova password per ${o.business_name}:`);
    if (!p || p.length < 6) return;
    try { await api.post(`/operators/${o.id}/reset-password`, { password: p }); toast.success("Password aggiornata"); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const toggleActive = async (o) => {
    try { await api.post(`/operators/${o.id}/${o.active ? "deactivate" : "activate"}`); toast.success(o.active ? "Disattivato" : "Riattivato"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const removeOp = async (o) => {
    if (!confirm(`Eliminera ${o.business_name} e TUTTI i suoi dati (${o.drivers_count} autisti, ${o.bookings_count} prenotazioni). Confermi?`)) return;
    try { await api.delete(`/operators/${o.id}`); toast.success("Operatore eliminato"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-slate-400 text-sm">Ogni operatore ha la propria area isolata: propri autisti, veicoli, clienti, prenotazioni, Twilio e conteggi.</div>
        <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="new-operator-btn"><Plus size={16} strokeWidth={3}/> Nuovo Operatore</Button>
      </div>
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
            <tr><th className="text-left p-3">Ragione Sociale</th><th className="text-left">Email</th><th className="text-right">Autisti</th><th className="text-right">Prenotazioni</th><th className="text-center">Stato</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-500">Nessun operatore</td></tr>}
            {rows.map(o => (
              <tr key={o.id} data-testid={`operator-row-${o.id}`}>
                <td className="p-3">
                  <div className="text-white font-bold">{o.business_name}</div>
                  <div className="text-xs text-slate-500">{o.contact_name || "-"}</div>
                </td>
                <td className="text-slate-400">{o.email}</td>
                <td className="text-right font-mono-num text-amber-400">{o.drivers_count}</td>
                <td className="text-right font-mono-num text-emerald-400">{o.bookings_count}</td>
                <td className="text-center">
                  {o.active
                    ? <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-xs uppercase tracking-widest">Attivo</span>
                    : <span className="px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 text-xs uppercase tracking-widest">Disattivo</span>}
                </td>
                <td className="pr-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={()=>openEdit(o)} className="p-1.5 text-slate-400 hover:text-amber-400" title="Modifica"><Edit2 size={14}/></button>
                    <button onClick={()=>resetPassword(o)} className="p-1.5 text-slate-400 hover:text-sky-400" title="Reset password" data-testid={`reset-op-${o.id}`}>Reset</button>
                    <button onClick={()=>toggleActive(o)} className="p-1.5 text-amber-400" title={o.active ? "Disattiva" : "Attiva"}>{o.active ? "Off" : "On"}</button>
                    <button onClick={()=>removeOp(o)} className="p-1.5 text-slate-400 hover:text-rose-400" title="Elimina"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl text-white">{editing ? "Modifica Operatore" : "Nuovo Operatore"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="Ragione Sociale *" className="col-span-2"><Input value={form.business_name} onChange={e=>setForm({...form, business_name:e.target.value})} className="bg-slate-900 border-slate-800" data-testid="op-business-name"/></Field>
            <Field label="Contatto"><Input value={form.contact_name || ""} onChange={e=>setForm({...form, contact_name:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
            <Field label="Telefono"><Input value={form.phone || ""} onChange={e=>setForm({...form, phone:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
            <Field label="Email login *" className="col-span-2"><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} className="bg-slate-900 border-slate-800" disabled={!!editing} data-testid="op-email"/></Field>
            <Field label="Partita IVA"><Input value={form.vat || ""} onChange={e=>setForm({...form, vat:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
            <Field label="Codice Fiscale"><Input value={form.fiscal_code || ""} onChange={e=>setForm({...form, fiscal_code:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
            <Field label="Indirizzo" className="col-span-2"><Input value={form.address || ""} onChange={e=>setForm({...form, address:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
            {!editing && (
              <Field label="Password iniziale *" className="col-span-2"><Input type="password" value={form.login_password} onChange={e=>setForm({...form, login_password:e.target.value})} className="bg-slate-900 border-slate-800" data-testid="op-password"/></Field>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={()=>setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="save-operator-btn">{busy ? "Salvataggio..." : "Salva"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DataPanel() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmAll, setConfirmAll] = useState("");

  const load = async () => { try { const r = await api.get("/admin/db-stats"); setStats(r.data); } catch(e){} };
  useEffect(() => { load(); }, []);

  const downloadBackup = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0,19).replace(/[T:]/g,"-");
      a.href = url; a.download = `gestione_ncc_backup_${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Backup scaricato");
    } catch(e){ toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const runServerBackup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/tasks/backup-now");
      toast.success(`Backup salvato sul server (mantengo ultimi ${r.data.kept})`);
    } catch(e){ toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const resetTransactional = async () => {
    if (confirmText !== "RESET") { toast.error("Digita RESET per confermare"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/reset-transactional", { confirm: "RESET" });
      toast.success(`Reset OK: ${Object.entries(r.data.deleted).map(([k,v])=>`${k}=${v}`).join(", ")}`);
      setConfirmText("");
      load();
    } catch(e){ toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const resetAll = async () => {
    if (confirmAll !== "RESET-ALL") { toast.error("Digita RESET-ALL per confermare"); return; }
    if (!confirm("Sei davvero sicuro? Verranno cancellati TUTTI i dati (anagrafiche incluse) tranne il tuo admin.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/reset-all", { confirm: "RESET-ALL" });
      toast.success(`Reset totale OK: ${JSON.stringify(r.data.deleted)}`);
      setConfirmAll("");
      load();
    } catch(e){ toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-mono-num mb-4">
          <DatabaseBackup size={14}/> Statistiche Database
        </div>
        {stats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(stats).map(([k,v]) => (
              <div key={k} className="rounded-xl border border-slate-800/80 bg-slate-950/50 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{k}</div>
                <div className="mt-1 font-mono-num text-2xl font-bold text-amber-400">{v}</div>
              </div>
            ))}
          </div>
        ) : <div className="text-slate-500">Caricamento...</div>}
      </div>

      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-400 font-mono-num mb-2">
          <Download size={14}/> Backup Dati
        </div>
        <div className="text-slate-300 text-sm mb-4">
          Scarica un backup completo del database in formato JSON. Il sistema esegue automaticamente un backup ogni giorno alle <span className="text-amber-400 font-mono-num">03:30</span> (Europe/Rome) e mantiene gli ultimi 14 giorni sul server.
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="download-backup-btn" onClick={downloadBackup} disabled={busy}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold">
            <Download size={14}/> Scarica backup ora
          </Button>
          <Button data-testid="server-backup-btn" variant="outline" onClick={runServerBackup} disabled={busy}
            className="border-emerald-500/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10">
            <DatabaseBackup size={14}/> Salva backup sul server
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-amber-400 font-mono-num mb-2">
          <AlertTriangle size={14}/> Reset Dati Transazionali
        </div>
        <div className="text-slate-300 text-sm mb-4">
          Cancella <span className="text-amber-400 font-bold">SOLO</span> prenotazioni, incassi, uber, scambi, preventivi e notifiche.
          <br/>Mantiene: clienti, autisti, veicoli, fornitori, utenti, impostazioni azienda.
          <br/>Utile per ripartire con un nuovo anno operativo mantenendo le anagrafiche.
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Digita RESET per confermare" value={confirmText} onChange={e=>setConfirmText(e.target.value)}
            className="bg-slate-900 border-slate-800 w-64" data-testid="confirm-reset-input"/>
          <Button data-testid="reset-transactional-btn" onClick={resetTransactional} disabled={busy || confirmText !== "RESET"}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold disabled:opacity-40">
            <Trash2 size={14}/> Reset dati transazionali
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-rose-500/40 bg-rose-500/5 p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-rose-400 font-mono-num mb-2">
          <AlertTriangle size={14}/> Reset Totale (PERICOLOSO)
        </div>
        <div className="text-slate-300 text-sm mb-4">
          Cancella <span className="text-rose-400 font-bold">TUTTO</span>: anagrafiche, prenotazioni, incassi, tutti gli utenti tranne il tuo admin.
          <br/>Da usare SOLO se vuoi ripartire da zero. <span className="text-amber-400">Scarica il backup prima!</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Digita RESET-ALL per confermare" value={confirmAll} onChange={e=>setConfirmAll(e.target.value)}
            className="bg-slate-900 border-slate-800 w-64" data-testid="confirm-reset-all-input"/>
          <Button data-testid="reset-all-btn" onClick={resetAll} disabled={busy || confirmAll !== "RESET-ALL"}
            className="bg-rose-600 hover:bg-rose-500 text-white font-bold disabled:opacity-40">
            <Trash2 size={14}/> Reset totale
          </Button>
        </div>
      </div>
    </div>
  );
}

function UsersPanel({ scopedToOperator = false, isCompanyAdmin = false }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email:"", name:"", password:"", role:"operatore" });

  const load = async () => { const r = await api.get("/users"); setUsers(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        const p = { name: form.name, role: form.role };
        if (form.password) p.password = form.password;
        await api.patch(`/users/${editing.id}`, p);
      } else {
        await api.post("/users", form);
      }
      toast.success("Salvato"); setOpen(false); load();
    } catch(e){ toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if(!confirm("Eliminare utente?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Eliminato"); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const sendResetLink = async (u) => {
    if(!confirm(`Inviare un link di reset password a ${u.email}?`)) return;
    try {
      const r = await api.post(`/users/${u.id}/send-reset-link`);
      toast.success(`Link di reset inviato a ${r.data.email}`);
    } catch(e){ toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      {scopedToOperator && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200">
          Stai gestendo gli utenti della <strong>tua azienda</strong>. Non puoi creare utenti con ruolo <em>Amministratore globale</em>.
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={()=>{setEditing(null); setForm({email:"",name:"",password:"",role:"operatore"}); setOpen(true);}} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="new-user-btn"><Plus size={16} strokeWidth={3}/> Nuovo Utente</Button>
      </div>
      <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">
            <tr><th className="text-left p-3">Nome</th><th className="text-left">Email</th><th className="text-left">Ruolo</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {users.map(u => (
              <tr key={u.id}>
                <td className="p-3 text-white">{u.name}</td>
                <td className="text-slate-400">{u.email}</td>
                <td><span className="px-2 py-0.5 rounded bg-slate-800 text-xs uppercase tracking-widest text-slate-300">{u.role}</span></td>
                <td className="pr-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={()=>sendResetLink(u)} title="Invia link di reset password via email" data-testid={`send-reset-${u.id}`} className="p-1.5 text-slate-400 hover:text-sky-400"><Mail size={14}/></button>
                    <button onClick={()=>{setEditing(u); setForm({...u, password:""}); setOpen(true);}} className="p-1.5 text-slate-400 hover:text-amber-400"><Edit2 size={14}/></button>
                    <button onClick={()=>remove(u.id)} className="p-1.5 text-slate-400 hover:text-rose-400"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 text-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl text-white">{editing ? "Modifica Utente" : "Nuovo Utente"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {!editing && <Field label="Email"><Input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>}
            <Field label="Nome"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
            <Field label={editing ? "Nuova password (facoltativa)" : "Password"}><Input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} className="bg-slate-900 border-slate-800"/></Field>
            <Field label="Ruolo">
              <Select value={form.role} onValueChange={v=>setForm({...form,role:v})}>
                <SelectTrigger className="bg-slate-900 border-slate-800"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800">
                  {!scopedToOperator && <SelectItem value="admin">Admin globale (piattaforma)</SelectItem>}
                  <SelectItem value="admin_azienda">Admin Azienda (titolare interno)</SelectItem>
                  <SelectItem value="operatore">Operatore</SelectItem>
                  <SelectItem value="autista">Autista</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300" onClick={()=>setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold" data-testid="save-user-btn">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
